import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, QrCode } from 'lucide-react';
import { LEGAL_CONTENT } from './legalData';

const MarkdownRenderer = ({ content }) => {
  const renderInline = (text) => {
    const parts = [];
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match;
    while ((match = boldRegex.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
      parts.push(<strong key={`b-${match.index}`}>{match[1]}</strong>);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 0 ? parts : [text];
  };

  const lines = content.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.replace(/^#+\s*/g, '').trim();
        if (line.match(/^#{3,}\s/)) return <h3 key={i} className="text-lg font-semibold mt-6 mb-2">{renderInline(trimmed)}</h3>;
        if (line.match(/^#{1,2}\s+#{1,2}\s/)) return <h2 key={i} className="text-xl font-bold mt-8 mb-3">{renderInline(line.replace(/^[#\s]+/, '').trim())}</h2>;
        if (line.match(/^#{2}\s/)) return <h2 key={i} className="text-xl font-bold mt-8 mb-3">{renderInline(trimmed)}</h2>;
        if (line.match(/^#\s/)) return <h1 key={i} className="text-2xl font-bold mt-8 mb-4">{renderInline(trimmed)}</h1>;
        if (line.startsWith('- ') || line.startsWith('* ') || line.match(/^[•●]\s/)) {
          const text = line.replace(/^[-*•●]\s+/, '');
          return <li key={i} className="ml-4 mb-1 text-muted-foreground list-disc">{renderInline(text)}</li>;
        }
        if (line.startsWith('---')) return <hr key={i} className="my-6 border-border" />;
        if (line.trim() === '') return <br key={i} />;
        return <p key={i} className="mb-2 text-muted-foreground leading-relaxed">{renderInline(line)}</p>;
      })}
    </div>
  );
};

const LandingLegal = () => {
  const { pageType } = useParams();
  const navigate = useNavigate();
  const page = LEGAL_CONTENT[pageType];

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold mb-3">Page non trouvee</h1>
          <Button onClick={() => navigate('/')} className="rounded-full">Retour a l'accueil</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="landing-legal-page">
      <header className="border-b sticky top-0 bg-background/95 backdrop-blur-sm z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <QrCode className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-extrabold font-['Manrope']">IziCatalog</span>
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="legal-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <MarkdownRenderer content={page.content} />
      </main>

      <footer className="border-t py-8 px-4">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
          <Link to="/legal/mentions-legales" className="hover:text-foreground transition-colors">Mentions legales</Link>
          <Link to="/legal/politique-confidentialite" className="hover:text-foreground transition-colors">Politique de confidentialite</Link>
          <Link to="/legal/cgu" className="hover:text-foreground transition-colors">CGU</Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingLegal;
